/**
 * Service pour gérer les liens Google Drive et télécharger les images
 */

class GoogleDriveService {
  
  /**
   * Convertir un lien Google Drive en lien de téléchargement direct
   * @param {string} driveUrl - URL Google Drive (sharing link ou file ID)
   * @returns {string} URL de téléchargement direct
   */
  convertToDirectDownloadUrl(driveUrl) {
    if (!driveUrl || typeof driveUrl !== 'string') {
      throw new Error('URL Google Drive invalide');
    }

    // Nettoyer l'URL
    const cleanUrl = driveUrl.trim();

    // Pattern 1: https://drive.google.com/file/d/FILE_ID/view?usp=sharing
    const viewPattern = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view/;
    const viewMatch = cleanUrl.match(viewPattern);
    if (viewMatch) {
      const fileId = viewMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    // Pattern 2: https://drive.google.com/open?id=FILE_ID
    const openPattern = /https:\/\/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/;
    const openMatch = cleanUrl.match(openPattern);
    if (openMatch) {
      const fileId = openMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    // Pattern 3: Juste l'ID du fichier
    const idPattern = /^[a-zA-Z0-9_-]{25,}$/;
    if (idPattern.test(cleanUrl)) {
      return `https://drive.google.com/uc?export=download&id=${cleanUrl}`;
    }

    // Pattern 4: URL déjà au bon format
    if (cleanUrl.includes('drive.google.com/uc?export=download')) {
      return cleanUrl;
    }

    // Si aucun pattern ne correspond, essayer d'extraire l'ID de n'importe où dans l'URL
    const anyIdMatch = cleanUrl.match(/([a-zA-Z0-9_-]{25,})/);
    if (anyIdMatch) {
      const fileId = anyIdMatch[1];
      return `https://drive.google.com/uc?export=download&id=${fileId}`;
    }

    throw new Error(`Format d'URL Google Drive non reconnu: ${cleanUrl}`);
  }

  /**
   * Vérifier si une URL est un lien Google Drive
   * @param {string} url - URL à vérifier
   * @returns {boolean}
   */
  isGoogleDriveUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    const cleanUrl = url.trim().toLowerCase();
    return cleanUrl.includes('drive.google.com') || 
           cleanUrl.includes('docs.google.com') ||
           /^[a-zA-Z0-9_-]{25,}$/.test(url.trim()); // ID seul
  }

  /**
   * Télécharger une image depuis Google Drive via le backend
   * @param {string} driveUrl - URL Google Drive
   * @param {string} filename - Nom du fichier de destination (optionnel)
   * @returns {Promise<Object>} {success: boolean, filepath?: string, error?: string}
   */
  async downloadImage(driveUrl, filename = null) {
    try {
      console.log(`🔄 Téléchargement via backend: ${driveUrl}`);

      // Utiliser l'endpoint backend pour contourner CORS
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/products/download-google-drive-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: driveUrl })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Erreur HTTP ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Erreur lors du téléchargement');
      }

      console.log(`✅ Image téléchargée: ${result.filepath}`);
      
      // Retourner un objet compatible avec l'ancienne interface
      return {
        success: true,
        filepath: result.filepath,
        filename: result.filename,
        originalUrl: result.originalUrl
      };

    } catch (error) {
      console.error('Erreur téléchargement Google Drive:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Télécharger un fichier (PDF/doc) via le backend
   */
  async downloadFile(driveUrl) {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/products/download-google-drive-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ url: driveUrl })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  }

  /**
   * Traiter plusieurs liens Google Drive en parallèle
   * @param {Array<string>} driveUrls - Liste des URLs Google Drive
   * @param {Function} onProgress - Callback de progression
   * @returns {Promise<Array<{success: boolean, filepath?: string, error?: string, originalUrl: string}>>}
   */
  async downloadMultipleImages(driveUrls, onProgress = null) {
    const total = driveUrls.length;
    console.log(`🚀 Téléchargement parallèle de ${total} images Google Drive...`);

    // Traitement en parallèle par lots de 5 pour éviter la surcharge
    const batchSize = 5;
    const results = [];

    for (let i = 0; i < driveUrls.length; i += batchSize) {
      const batch = driveUrls.slice(i, i + batchSize);
      
      // Traiter le lot en parallèle
      const batchPromises = batch.map(async (url, index) => {
        const globalIndex = i + index;
        
        try {
          if (!this.isGoogleDriveUrl(url)) {
            return {
              success: false,
              error: 'URL Google Drive invalide',
              originalUrl: url
            };
          }

          const result = await this.downloadImage(url);
          
          if (result.success) {
            console.log(`✅ Image ${globalIndex + 1}/${total} téléchargée avec succès`);
            return {
              success: true,
              filepath: result.filepath,
              filename: result.filename,
              originalUrl: url
            };
          } else {
            console.error(`❌ Erreur image ${globalIndex + 1}:`, result.error);
            return {
              success: false,
              error: result.error,
              originalUrl: url
            };
          }

        } catch (error) {
          console.error(`❌ Erreur image ${globalIndex + 1}:`, error);
          return {
            success: false,
            error: error.message,
            originalUrl: url
          };
        }
      });

      // Attendre que le lot soit terminé
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Mettre à jour la progression
      if (onProgress) {
        const progress = Math.round(((i + batch.length) / total) * 100);
        onProgress(progress, i + batch.length, total);
      }

      // Pause plus courte entre les lots
      if (i + batchSize < driveUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return results;
  }

  /**
   * Valider et préparer une URL Google Drive
   * @param {string} url - URL à valider
   * @returns {Object} {isValid: boolean, directUrl?: string, error?: string}
   */
  validateAndPrepareUrl(url) {
    try {
      if (!this.isGoogleDriveUrl(url)) {
        return {
          isValid: false,
          error: 'Ce n\'est pas un lien Google Drive valide'
        };
      }

      const directUrl = this.convertToDirectDownloadUrl(url);
      
      return {
        isValid: true,
        directUrl: directUrl
      };

    } catch (error) {
      return {
        isValid: false,
        error: error.message
      };
    }
  }

  /**
   * Extraire l'ID du fichier depuis une URL Google Drive
   * @param {string} url - URL Google Drive
   * @returns {string|null} ID du fichier ou null si non trouvé
   */
  extractFileId(url) {
    try {
      const patterns = [
        /\/file\/d\/([a-zA-Z0-9_-]+)/,
        /[?&]id=([a-zA-Z0-9_-]+)/,
        /^([a-zA-Z0-9_-]{25,})$/
      ];

      for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }
}

// Instance singleton
export const googleDriveService = new GoogleDriveService();
export default googleDriveService;
